import { MAIN_PATH } from "../routes";

const PLAY_ROUTE_STORAGE_KEY = "sliding-master-play-route-v1";

/**
 * Persist play navigation payload so `slidingId` survives WebView reloads / lost `location.state`.
 * @param {object} state
 */
export function savePlayRouteState(state) {
    if (!state || typeof state !== "object") return;
    try {
        sessionStorage.setItem(PLAY_ROUTE_STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* private mode / quota */
    }
}

/** @returns {object|null} */
export function loadPlayRouteState() {
    try {
        const raw = sessionStorage.getItem(PLAY_ROUTE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

/**
 * Merge React Router `location.state`, `?slidingId=` query, and sessionStorage.
 * @param {object|null|undefined} locationState
 * @param {URLSearchParams|null|undefined} searchParams
 * @returns {object}
 */
export function hydratePlayRouteState(locationState, searchParams) {
    const stored = loadPlayRouteState();
    const fromLoc = locationState && typeof locationState === "object" ? locationState : {};
    const fromQuery = (searchParams?.get("slidingId") || "").trim();

    const slidingId =
        (typeof fromLoc.slidingId === "string" && fromLoc.slidingId.trim()) ||
        fromQuery ||
        (typeof stored?.slidingId === "string" && stored.slidingId.trim()) ||
        "";

    const hasFreshNav = Object.keys(fromLoc).length > 0;
    const merged = hasFreshNav
        ? { ...fromLoc, ...(slidingId ? { slidingId } : {}) }
        : { ...(stored || {}), ...(slidingId ? { slidingId } : {}) };

    const mergedSid = String(merged.slidingId || "").trim();
    const storedSid = String(stored?.slidingId || "").trim();
    /** Different puzzle than last stored play — not a resume (WebView often drops `location.state`). */
    if (mergedSid && storedSid && mergedSid !== storedSid) {
        merged.resume = false;
        if (!hasFreshNav || !("layout" in fromLoc)) {
            delete merged.layout;
        }
    }

    if (hasFreshNav && !("layout" in fromLoc)) {
        delete merged.layout;
        merged.resume = false;
    }

    if (hasFreshNav && fromLoc.resume !== true) {
        merged.resume = false;
    }

    return merged;
}

/** Attach where Play back should return (e.g. Library vs Home). */
export function attachPlayReturnPath(playState, returnPath) {
    if (!playState || typeof playState !== "object") return playState;
    const path = typeof returnPath === "string" ? returnPath.trim() : "";
    if (!path.startsWith("/")) return playState;
    return { ...playState, returnPath: path };
}

/** @param {object|null|undefined} state @param {string} [defaultPath] */
export function resolvePlayReturnPath(state, defaultPath = MAIN_PATH) {
    const fromState = typeof state?.returnPath === "string" ? state.returnPath.trim() : "";
    if (fromState.startsWith("/")) return fromState;
    const fallback = typeof defaultPath === "string" ? defaultPath.trim() : "";
    return fallback.startsWith("/") ? fallback : MAIN_PATH;
}

/**
 * Navigate helper: persist + optional query param for template id.
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {string} playPath
 * @param {object} state
 */
export function navigateToPlay(navigate, playPath, state) {
    if (!state || typeof state !== "object") {
        navigate(playPath);
        return;
    }
    savePlayRouteState(state);
    const sid = typeof state.slidingId === "string" ? state.slidingId.trim() : "";
    if (sid) {
        navigate(`${playPath}?slidingId=${encodeURIComponent(sid)}`, { state });
        return;
    }
    navigate(playPath, { state });
}
